import { useCallback, useEffect, useRef } from 'react';

/**
 * While a card preview is hovered we pause the page's hero/teaser video, then resume it on leave.
 *
 * The pause is delayed by `delay` (same default as useDelayedHover), so a quick pass-over a card
 * doesn't momentarily pause the teaser — it only pauses once the preview actually takes over.
 * `resumeHero` cancels a still-pending pause, so a cancelled hover never touches the teaser at all.
 *
 * The resume only happens if *we* paused it: a teaser the user already paused is left untouched, so
 * its play/pause controls don't flip state. Targets the first <video> on the page (the teaser),
 * matching the previous inline behaviour in the live/episode cards.
 */
export default function usePauseHeroVideo(delay = 300) {
  const pausedByUsRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPending = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const pauseHero = useCallback(() => {
    clearPending();
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      const hero = document.getElementsByTagName('video')[0];
      if (hero && !hero.paused) {
        hero.pause();
        pausedByUsRef.current = true;
      }
    }, delay);
  }, [delay]);

  const resumeHero = useCallback(() => {
    clearPending();
    if (!pausedByUsRef.current) return;
    pausedByUsRef.current = false;
    document.getElementsByTagName('video')[0]?.play()?.catch(() => {});
  }, []);

  useEffect(() => clearPending, []);

  return { pauseHero, resumeHero };
}
